// ============================================================================
// CrewAhead Maritime Platform - Zustand UI Store
// ============================================================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ─── Types ───────────────────────────────────────────────────────────────────

export type Theme = 'light' | 'dark' | 'system';

export type ModalId =
  | 'profile-edit'
  | 'certification-form'
  | 'sea-service-form'
  | 'document-upload'
  | 'job-apply'
  | 'job-alert-form'
  | 'confirm-delete'
  | 'availability-post-form'
  | 'settings'
  | null;

export type SidebarView = 'default' | 'collapsed' | 'expanded';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

export interface DrawerState {
  isOpen: boolean;
  content: 'notifications' | 'messages' | 'settings' | null;
}

export interface UIState {
  // ─── Theme ────────────────────────────────────────────────────────────────
  theme: Theme;
  systemTheme: 'light' | 'dark';

  // ─── Sidebar ──────────────────────────────────────────────────────────────
  sidebarOpen: boolean;
  sidebarView: SidebarView;
  mobileSidebarOpen: boolean;

  // ─── Modals ───────────────────────────────────────────────────────────────
  activeModal: ModalId;
  modalData: Record<string, unknown> | null;

  // ─── Drawers ──────────────────────────────────────────────────────────────
  drawer: DrawerState;

  // ─── Toasts ───────────────────────────────────────────────────────────────
  toasts: ToastMessage[];

  // ─── Page State ───────────────────────────────────────────────────────────
  pageTitle: string;
  pageBreadcrumbs: Array<{ label: string; href?: string }>;
  isPageLoading: boolean;

  // ─── Scroll ───────────────────────────────────────────────────────────────
  scrollPosition: number;

  // ─── Actions ──────────────────────────────────────────────────────────────
  // Theme
  setTheme: (theme: Theme) => void;
  setSystemTheme: (theme: 'light' | 'dark') => void;
  toggleTheme: () => void;

  // Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarView: (view: SidebarView) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;

  // Modals
  openModal: (modalId: ModalId, data?: Record<string, unknown>) => void;
  closeModal: () => void;

  // Drawers
  openDrawer: (content: DrawerState['content']) => void;
  closeDrawer: () => void;
  toggleDrawer: (content: DrawerState['content']) => void;

  // Toasts
  addToast: (toast: Omit<ToastMessage, 'id'>) => void;
  removeToast: (toastId: string) => void;
  clearToasts: () => void;

  // Page
  setPageTitle: (title: string) => void;
  setPageBreadcrumbs: (crumbs: Array<{ label: string; href?: string }>) => void;
  setPageLoading: (loading: boolean) => void;

  // Scroll
  setScrollPosition: (position: number) => void;
}

// ─── Theme Helpers ───────────────────────────────────────────────────────────

/**
 * Resolves the effective theme based on user preference and system setting.
 */
function resolveTheme(theme: Theme, systemTheme: 'light' | 'dark'): 'light' | 'dark' {
  if (theme === 'system') return systemTheme;
  return theme;
}

/**
 * Applies the theme to the document root element.
 * Avoids unnecessary class removal/re-addition so the class-token order
 * stays stable and matches the server-rendered <html> class.
 */
function applyThemeToDOM(theme: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const other = theme === 'dark' ? 'light' : 'dark';
  root.classList.remove(other);
  if (!root.classList.contains(theme)) {
    root.classList.add(theme);
  }

  // Set meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      theme === 'dark' ? '#0a1929' : '#ffffff'
    );
  }
}

// ─── UI Store ────────────────────────────────────────────────────────────────

/**
 * Zustand store for UI state — sidebar, modals, drawers, toasts, theme.
 *
 * Persists theme and sidebar preferences to localStorage.
 * All other state (modals, toasts) is ephemeral (not persisted).
 *
 * @example
 * ```tsx
 * // Sidebar
 * const sidebarOpen = useUIStore((s) => s.sidebarOpen);
 * const toggleSidebar = useUIStore((s) => s.toggleSidebar);
 *
 * // Theme
 * const theme = useUIStore((s) => s.theme);
 * const setTheme = useUIStore((s) => s.setTheme);
 *
 * // Modals
 * const openModal = useUIStore((s) => s.openModal);
 * openModal('profile-edit', { userId: '123' });
 *
 * // Toasts
 * const addToast = useUIStore((s) => s.addToast);
 * addToast({ type: 'success', title: 'Profile saved!' });
 *
 * // Page title
 * const setPageTitle = useUIStore((s) => s.setPageTitle);
 * setPageTitle('Dashboard');
 * ```
 */
export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // ─── Initial State ────────────────────────────────────────────────────

      // Theme
      theme: 'dark',
      systemTheme: 'dark',

      // Sidebar
      sidebarOpen: true,
      sidebarView: 'default',
      mobileSidebarOpen: false,

      // Modals
      activeModal: null,
      modalData: null,

      // Drawers
      drawer: { isOpen: false, content: null },

      // Toasts
      toasts: [],

      // Page
      pageTitle: '',
      pageBreadcrumbs: [],
      isPageLoading: false,

      // Scroll
      scrollPosition: 0,

      // ─── Theme Actions ────────────────────────────────────────────────────

      setTheme: (theme): void => {
        const effective = resolveTheme(theme, get().systemTheme);
        applyThemeToDOM(effective);
        set({ theme });
      },

      setSystemTheme: (systemTheme): void => {
        const { theme } = get();
        if (theme === 'system') {
          applyThemeToDOM(systemTheme);
        }
        set({ systemTheme });
      },

      toggleTheme: (): void => {
        const current = get().theme;
        const next: Theme =
          current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
        const effective = resolveTheme(next, get().systemTheme);
        applyThemeToDOM(effective);
        set({ theme: next });
      },

      // ─── Sidebar Actions ──────────────────────────────────────────────────

      toggleSidebar: (): void => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      setSidebarOpen: (open): void => {
        set({ sidebarOpen: open });
      },

      setSidebarView: (view): void => {
        set({ sidebarView: view });
      },

      toggleMobileSidebar: (): void => {
        set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen }));
      },

      setMobileSidebarOpen: (open): void => {
        set({ mobileSidebarOpen: open });
      },

      // ─── Modal Actions ────────────────────────────────────────────────────

      openModal: (modalId, data?): void => {
        set({ activeModal: modalId, modalData: data ?? null });
        // Lock body scroll when modal opens
        if (typeof document !== 'undefined') {
          document.body.style.overflow = 'hidden';
        }
      },

      closeModal: (): void => {
        set({ activeModal: null, modalData: null });
        // Unlock body scroll
        if (typeof document !== 'undefined') {
          document.body.style.overflow = '';
        }
      },

      // ─── Drawer Actions ───────────────────────────────────────────────────

      openDrawer: (content): void => {
        set({ drawer: { isOpen: true, content } });
      },

      closeDrawer: (): void => {
        set({ drawer: { isOpen: false, content: null } });
      },

      toggleDrawer: (content): void => {
        const { drawer } = get();
        if (drawer.isOpen && drawer.content === content) {
          set({ drawer: { isOpen: false, content: null } });
        } else {
          set({ drawer: { isOpen: true, content } });
        }
      },

      // ─── Toast Actions ────────────────────────────────────────────────────

      addToast: (toast): void => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }].slice(-5), // Keep max 5
        }));

        // Auto-dismiss
        const duration = toast.duration ?? 5000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      removeToast: (toastId): void => {
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== toastId),
        }));
      },

      clearToasts: (): void => {
        set({ toasts: [] });
      },

      // ─── Page Actions ─────────────────────────────────────────────────────

      setPageTitle: (title): void => {
        set({ pageTitle: title });
        // Also update document title
        if (typeof document !== 'undefined') {
          document.title = title ? `${title} | CrewAhead` : 'CrewAhead';
        }
      },

      setPageBreadcrumbs: (crumbs): void => {
        set({ pageBreadcrumbs: crumbs });
      },

      setPageLoading: (loading): void => {
        set({ isPageLoading: loading });
      },

      // ─── Scroll Actions ───────────────────────────────────────────────────

      setScrollPosition: (position): void => {
        set({ scrollPosition: position });
      },
    }),
    {
      name: 'crew-ahead-ui-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist theme and sidebar preferences
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        sidebarView: state.sidebarView,
      }),
      // Apply theme on rehydration
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error && state) {
            const effective = resolveTheme(state.theme, state.systemTheme);
            applyThemeToDOM(effective);
          }
        };
      },
    }
  )
);

// ─── Selectors ───────────────────────────────────────────────────────────────

/**
 * Selector to get the effective theme (resolves 'system' to light/dark).
 */
export const selectEffectiveTheme = (state: UIState): 'light' | 'dark' => {
  return resolveTheme(state.theme, state.systemTheme);
};

/**
 * Selector to check if any modal is currently open.
 */
export const selectIsModalOpen = (state: UIState): boolean => {
  return state.activeModal !== null;
};

/**
 * Selector to check if any drawer is currently open.
 */
export const selectIsDrawerOpen = (state: UIState): boolean => {
  return state.drawer.isOpen;
};

/**
 * Selector to get active drawer content type.
 */
export const selectDrawerContent = (
  state: UIState
): DrawerState['content'] => {
  return state.drawer.content;
};

/**
 * Selector to check if the UI is in a loading state.
 */
export const selectIsLoading = (state: UIState): boolean => {
  return state.isPageLoading;
};
